import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Grid, Modal } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { LoadingButton } from '@mui/lab';
import { FileDownloadOutlined } from '@mui/icons-material';
import { MultiSelectWithSearch } from 'src/components/multi-select-with-search';
import SucessModal from 'src/components/sucess-modal';
import { getPullServiceLog } from 'src/services/serviceLogMatrixService/getPullServiceLog';
import { getSLMBatchRetrieve } from 'src/services/serviceLogMatrixService/getSLMBatchRetrieve';
import {
  IServiceLogGetParams,
  TRetrieveBatchGetParams,
} from 'src/services/serviceLogMatrixService/types';
import _ from 'lodash';
import moment from 'moment';

const SlmReports = () => {
  const getFacilityUuid = localStorage.getItem('facilityId');

  const [defaultDate, setDefaultDate] = useState<Date>(new Date());
  const [dateToApiCall, setDateToApiCall] = useState<{ year: string; month: string }>({
    year: '',
    month: '',
  });
  const [searchString, setSearchString] = useState<string>('');
  const [patientsParam, setPatientsParam] = useState<string>('');
  const [isAllPatientsSelected, setIsAllPatientsSelected] = useState<boolean>(false);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const [snackbarError, setSnackbarError] = useState<boolean>(false);
  const [snackbarSuccess, setSnackbarSuccess] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    title: '',
  });
  const [facilityName, setFacilityName] = useState<string>('');
  const [patientsFromBatch, setPatientsFromBatch] = useState<{ label: string; value: unknown }[]>(
    []
  );
  const [lastUpdatedDate, setLastUpdatedDate] = useState<string | Date | null>(new Date());  
  const [totalElements, setTotalElements] = useState<number>(0);
  const [elementsLimit, setElementsLimit] = useState<number>(0);
  const [retrieveBatchLoading, setRetrieveBatchLoading] = useState<boolean>(false);
  const [resetValues, setResetValues] = useState<boolean>(false);
  const [patientValues, setPatientValues] = useState<unknown[]>([]);

  const getAssignedFacilities = useSelector((state: any) => state.getFacilityDropDownReducer);

  const handleChangeDate = (date: Date | null) => {
    setPatientsParam('');
    setIsAllPatientsSelected(false);
    setSearchString('');
    setTotalElements(0);
    setElementsLimit(0);
    setPatientsFromBatch([]);
    setResetValues(true);

    const check = moment(date, 'YYYY/MM/DD');
    const newDate = { month: check.format('M'), year: check.format('YYYY') };

    setDateToApiCall({ ...newDate });
  };

  const getDefaultDate = useCallback(() => {
    // Get default date (previous month):
    const previousMonth = new Date();
    previousMonth.setDate(1);
    previousMonth.setMonth(previousMonth.getMonth() - 1);

    setDefaultDate(previousMonth);

    const dateToApi = moment(new Date()).subtract(1, 'months').endOf('month');

    const newDate = { month: dateToApi.format('M'), year: dateToApi.format('YYYY') };
    setDateToApiCall({ ...newDate });
  }, []);

  useEffect(() => {
    getDefaultDate();
  }, [getDefaultDate]);

  // Retrieve Batch Endpoint
  const getBatch = useCallback(
    (
      year: string,
      month: string,
      search?: string,
      page?: number | string,
      limit?: number | string
    ) => {
      const batchRequestParams: TRetrieveBatchGetParams = {
        year,
        month,
        enterprise_name: '',
      };
      if (search?.length) batchRequestParams.search = search;
      if (page) batchRequestParams.page = page;
      if (limit) batchRequestParams.limit = limit;

      setRetrieveBatchLoading(true);

      getSLMBatchRetrieve(batchRequestParams)
        .then((response) => {
          
          const lastModified = moment.utc(response.data.batch_information.modified);

          const localMoment = lastModified.local();

          const formattedLocalTime = localMoment.format('MM/DD/YYYY, h:mm A');

          setLastUpdatedDate(formattedLocalTime);

          const patients = Object.values(response.data.egress_information).map((patient: any) => {
            return {
              label: `${patient.patient_information.last_name}, ${patient.patient_information.first_name} (${patient.patient_information.medical_record_number})`,
              value: patient.patient_information.id,
            };
          });
          setPatientsFromBatch(patients);
          setTotalElements(response.data.pagination.total);
          setElementsLimit((l) => l + 10);
          setSnackbarError(false);
          setRetrieveBatchLoading(false);
        })
        .catch((error) => {
          setSnackbarSuccess(false);

          if (error.response.status !== 404) {
            setSnackbarError(true);

            setSnackbar((snackbar) => ({
              ...snackbar,
              open: true,
              title: '',
              message:
                'Something went wrong with retrieving the SLM. Please refresh or let our support team know if the issue persists.',
            }));
          }
          setPatientsFromBatch([]);
          setPatientsParam('');
          setIsAllPatientsSelected(false);
          setRetrieveBatchLoading(false);
        })
        .finally(() => {
          setRetrieveBatchLoading(false);
          setResetValues(false);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateToApiCall?.year, dateToApiCall?.month]
  );

  useEffect(() => {
    const debounceSearch = _.debounce(() => {
      getBatch(
        dateToApiCall?.year ?? '',
        dateToApiCall?.month ?? '',
        searchString?.trim()?.split('-')?.join('')
      );
    }, 1000);
    debounceSearch();

    return () => {
      debounceSearch.cancel();
      setTotalElements(0);
      setElementsLimit(0);
    };
  }, [getBatch, dateToApiCall?.year, dateToApiCall?.month, searchString]);

  const handleDownload = () => {
    setDownloadLoading(true);

    const requestParams: IServiceLogGetParams = {
      year: dateToApiCall?.year ?? '',
      month: dateToApiCall?.month ?? '',
      enterprise_name: '',
    };

    if (!isAllPatientsSelected && patientsParam.length) {
      const arrayOfIds = patientsParam.split(',');
      requestParams.patients_uuids = arrayOfIds;
    }

    getPullServiceLog(requestParams)
      .then((res) => {
        setDownloadLoading(false);
        const trimmedFacilityName = facilityName.trim();
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `${trimmedFacilityName}_${dateToApiCall.month}_${dateToApiCall.year}_slms.zip`
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);

        setSnackbarSuccess(true);
        setSnackbarError(false);
        //format date to snackbar message:
        const selectedMonth = moment(dateToApiCall?.month, 'MM').format('MMMM');
        const selectedYear = moment(dateToApiCall?.year, 'YY').format('YYYY');

        setSnackbar({
          ...snackbar,
          open: true,
          title: 'SLMs Downloaded!',
          message: `${facilityName} ${selectedMonth} ${selectedYear} SLMs successfully downloaded!`,
        });
      })
      .catch((error) => {
        setSnackbarError(true);
        setSnackbarSuccess(false);
        setSnackbar({
          ...snackbar,
          open: true,
          title: 'Error Downloading SLMs',
          message:
            'Something went wrong with downloading the SLMs. Please refresh or let us know via Intercom if the issue persists.',
        });
      })
      .finally(() => {
        setDownloadLoading(false);
      });
  };

  const getFacilityName = useCallback(() => {
    const facility = getAssignedFacilities.response?.data?.data?.find((fac: any) => {
      if (fac.uuid === getFacilityUuid) {
        return fac;
      }
    });

    return facility.name;
  }, [getAssignedFacilities, getFacilityUuid]);

  useEffect(() => {
    const name = getFacilityName();
    setFacilityName(name);
    return () => {
      setFacilityName('');
    };
  }, [getFacilityName]);

  useEffect(() => {
    const selectedPatientValues = patientsFromBatch.map(
      (patient: { label: string; value: unknown }) => patient.value
    );
    setPatientValues(selectedPatientValues);

    return () => {
      setPatientValues([]);
    };
  }, [patientsFromBatch]);

  const handleScroll = () => {
    getBatch(
      dateToApiCall?.year ?? '',
      dateToApiCall?.month ?? '',
      undefined,
      undefined,
      elementsLimit
    );
  };

  // Get whether the rendered patients are the total of available
  // through the API response
  const shouldRunApiCall = elementsLimit - totalElements === 10 || totalElements === elementsLimit;

  const text1 =
    'Data in SLMs for the current and previous months is automatically updated \nnightly. No action is needed on your part.';
  const text2 =
    'SLMs from earlier months are available for download, but if you made a recent change to a \ndocument, their data is not updated automatically.';
  const text3 =
    'If you require updated SLM data for an earlier month, please submit a \nrequest via Intercom.';

  const renderPatientsDropdownLabelPlaceholder = (): string => {
    if (isAllPatientsSelected) {
      return 'All Patients';
    } else {
      const selectedValues = patientsFromBatch.filter((patient) =>
        patientsParam.split(',').includes(String(patient.value))
      );

      const selectedLabels = selectedValues.map((patient) => patient.label).join(', ');
      const areLabelsSelected = selectedLabels
        ? `(${selectedValues.length}) ${selectedLabels}`
        : null;
      return areLabelsSelected || 'Patients';
    }
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Typography
        sx={{ marginBottom: 2 }}
        variant="h4"
      >
        Reports
      </Typography>
      <Typography
        sx={{ marginBottom: 1 }}
        variant="h5"
      >
        Service Log Matrix
      </Typography>

      <div style={{ whiteSpace: 'pre' }}>
        <Typography variant="body2">
          <span style={{ fontWeight: 'bold' }}>Current & Previous Month:</span> {text1}
        </Typography>
        <Typography variant="body2">
          <span style={{ fontWeight: 'bold' }}>Older Months:</span> {text2}
        </Typography>
        <Typography variant="body2">
          <span style={{ fontWeight: 'bold' }}>Need Updates for Older Months?</span> {text3}
        </Typography>
      </div>

      <Grid
        container
        spacing={2}
        alignItems="flex-end"
        marginTop="8px"
      >
        <Grid item>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              views={['month', 'year']}
              value={defaultDate}
              onChange={handleChangeDate}
              maxDate={new Date()}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'outlined',
                },
              }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item>
          <MultiSelectWithSearch
            sx={{
              border: '1px solid #E5E7EB',
              width: '250px',
              boxShadow: '0px 3px 14px 0px rgba(0, 0, 0, 0.08)',
              display: 'flex',
              justifyContent: 'start',
            }}
            label={renderPatientsDropdownLabelPlaceholder()}
            options={patientsFromBatch}
            value={patientValues}
            selectAll
            selectAllLabel="All Patients"
            withSearch
            searchString={searchString}
            setSearchString={setSearchString}
            onScroll={handleScroll}
            loading={retrieveBatchLoading}
            shouldRunApiCall={shouldRunApiCall}
            isAllSelected={isAllPatientsSelected}
            setIsAllSelected={setIsAllPatientsSelected}
            setSelectedValuesString={setPatientsParam}
            resetValues={resetValues}
          />
        </Grid>

        <Grid item>
          <LoadingButton
            disabled={downloadLoading || !patientValues.length || !patientsParam.length}
            startIcon={<FileDownloadOutlined />}
            variant="contained"
            size="small"
            sx={{
              whiteSpace: 'nowrap',
              height: 35,
              width: 120,
              ':hover': {
                background: '#6366F1',
              },
              '.MuiLoadingButton-loadingIndicator': {
                left: 15,
              },
            }}
            loadingPosition="start"
            onClick={handleDownload}
            loading={downloadLoading}
          >
            Download
          </LoadingButton>
        </Grid>
      </Grid>

      <Typography
        variant="body2"
        sx={{ marginTop: 2 }}
      >
        {patientValues.length === 0 && lastUpdatedDate
          ? 'No SLMs available for the selected month.'
          : `Data last updated on: ${lastUpdatedDate}`}
      </Typography>
      {downloadLoading && <Typography variant="body2">Downloading SLM...</Typography>}

      <Modal open={snackbar.open}>
        <SucessModal
          close={() => setSnackbar({ ...snackbar, open: false })}
          title={snackbar.title}
          message={snackbar.message}
          isSucess={snackbarSuccess && !snackbarError}
        />
      </Modal>
    </Box>
  );
};

export default SlmReports;
